
export const buildLoggedUser = (user) => {

    return {
        ...user,
        lastName: user.last_name,
        profile: user.profile
    }
}

// Role-based permissions configuration
// Define which routes are accessible for each role
const ROLE_PERMISSIONS = {
    commercial: {
        allowedRoutes: ['/activeServices']
    },
    admin: {
        allowedRoutes: 'all' // Admin has access to all routes
    },
    operator: {
        allowedRoutes: 'all'
    },
    stock: {
        allowedRoutes: 'all'
    },
    monitor: {
        allowedRoutes: 'all'
    },
    laboratory: {
        allowedRoutes: 'all'
    },
    installer: {
        allowedRoutes: 'all'
    },
    agent: {
        allowedRoutes: 'all'
    },
    account: {
        allowedRoutes: 'all'
    }
};

// Rutas que solo ve el perfil admin. Hace falta una lista aparte porque casi
// todos los roles de ROLE_PERMISSIONS tienen allowedRoutes: 'all' y sin esto
// pasarian el chequeo normal.
export const ADMIN_ONLY_ROUTES = ['/deviceCommands'];

const getUserRole = (loggedUser) => {
    if (!loggedUser || !loggedUser.profile || loggedUser.profile.length === 0) {
        return null;
    }

    return typeof loggedUser.profile[0] === 'object'
        ? loggedUser.profile[0].code || loggedUser.profile[0].name
        : loggedUser.profile[0];
};

/**
 * Check if a user has the admin role
 * @param {Object} loggedUser - The logged in user object
 * @returns {boolean} - True if user is admin, false otherwise
 */
export const isAdminRole = (loggedUser) => getUserRole(loggedUser)?.toLowerCase() === 'admin';

/**
 * Check if a user has access to a specific route based on their role
 * @param {Object} loggedUser - The logged in user object
 * @param {string} routePath - The route path to check
 * @returns {boolean} - True if user has access, false otherwise
 */
export const hasRouteAccess = (loggedUser, routePath) => {
    // Always allow access to authentication routes (login, logout)
    if (routePath === '/login' || routePath === '/logout' || routePath.startsWith('/auth')) {
        return true;
    }

    if (ADMIN_ONLY_ROUTES.some((adminRoute) => routePath.startsWith(adminRoute))) {
        return isAdminRole(loggedUser);
    }

    if (!loggedUser || !loggedUser.profile || loggedUser.profile.length === 0) {
        return false;
    }

    // Get the user's role (assuming first profile if multiple)
    const userRole = typeof loggedUser.profile[0] === 'object' 
        ? loggedUser.profile[0].code || loggedUser.profile[0].name 
        : loggedUser.profile[0];

    const rolePermission = ROLE_PERMISSIONS[userRole?.toLowerCase()];

    // If role not found in permissions, deny access by default
    if (!rolePermission) {
        return false;
    }

    // If role has access to all routes
    if (rolePermission.allowedRoutes === 'all') {
        return true;
    }

    // Check if the route is in the allowed routes list
    return rolePermission.allowedRoutes.some(allowedRoute => 
        routePath.startsWith(allowedRoute)
    );
};

/**
 * Check if a user is a commercial role
 * @param {Object} loggedUser - The logged in user object
 * @returns {boolean} - True if user is commercial, false otherwise
 */
export const isCommercialRole = (loggedUser) => {
    if (!loggedUser || !loggedUser.profile || loggedUser.profile.length === 0) {
        return false;
    }

    const userRole = typeof loggedUser.profile[0] === 'object' 
        ? loggedUser.profile[0].code || loggedUser.profile[0].name 
        : loggedUser.profile[0];

    return userRole?.toLowerCase() === 'commercial';
};
